import { TableBody, TableCell, TableRow } from '@mui/material';
import { dateToLabel, numberToCurrencyString } from 'further-ui/utils';
import { round, sumBy } from 'lodash';
import { Control } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import AllocationPercentageCell from './AllocationPercentageCell';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { InvestmentAllocation } from '../../../types';
import {
  InvestorCell,
  HeaderCell,
  StyledRow,
  FooterCell,
  FooterContainer,
  StyledHeader,
  StyledTable,
} from 'components/TableComponents/TableComponents';
import useSyncInvestorAllocations from '../../../hooks/useSyncInvestorAllocations';

type Props = {
  currency: CurrencyCode;
  considerationIndex: number;
  investmentAllocation: Array<InvestmentAllocation>;
  flatInvestmentAllocations: Array<InvestmentAllocation>;
  control: Control<rules.CreateRealisationFormValues>;
  isFirstTable: boolean;
};

const InvestorAllocationsTable: React.FC<Props> = ({
  currency,
  considerationIndex,
  investmentAllocation,
  flatInvestmentAllocations,
  control,
  isFirstTable,
}) => {
  const { setInvestorAllocations, setInvestorAllocationType } =
    useSyncInvestorAllocations();
  const holdingClassName = investmentAllocation[0].holdingClassName;
  return (
    <StyledTable
      hasBorderTopLeftRadius={isFirstTable}
      hasBorderBottomLeftRadius={isFirstTable}
    >
      <StyledHeader>
        <StyledRow
          key={`${investmentAllocation[0].investmentId}-${investmentAllocation[0].holdingClassId}-row-1`}
        >
          {isFirstTable && <InvestorCell colSpan={1} isHeader></InvestorCell>}
          <HeaderCell
            colSpan={5}
            align="center"
            isHeader
            textSize="regular"
            paddingSize="md"
          >
            {holdingClassName}
          </HeaderCell>
        </StyledRow>
      </StyledHeader>
      <StyledHeader color="paper">
        <StyledRow
          key={`${investmentAllocation[0].investmentId}-${investmentAllocation[0].holdingClassId}-row-2`}
        >
          {isFirstTable && <InvestorCell isHeader>Investor</InvestorCell>}
          <HeaderCell isHeader>{holdingClassName} %</HeaderCell>
          <HeaderCell isHeader>Received consideration</HeaderCell>
          <HeaderCell isHeader>Escrow consideration</HeaderCell>
          <HeaderCell isHeader>Earnout consideration</HeaderCell>
          <HeaderCell isHeader>Withholding tax</HeaderCell>
        </StyledRow>
      </StyledHeader>
      <TableBody>
        {investmentAllocation.map((row) => (
          <StyledRow
            key={`${investmentAllocation[0].investmentId}-${investmentAllocation[0].holdingClassId}-${row.commitmentId}-row-3`}
          >
            {isFirstTable && <InvestorCell>{row.investorName}</InvestorCell>}
            <TableCell>
              <AllocationPercentageCell
                setInvestorAllocations={() =>
                  setInvestorAllocations(considerationIndex)
                }
                considerationIndex={considerationIndex}
                setInvestorAllocationType={setInvestorAllocationType}
                investmentId={row.investmentId}
                allocationIndex={
                  flatInvestmentAllocations?.findIndex(
                    (allocation) =>
                      allocation.investmentId === row.investmentId &&
                      allocation.holdingClassId === row.holdingClassId &&
                      allocation.commitmentId === row.commitmentId,
                  ) ?? 0
                }
                control={control}
              />
            </TableCell>
            <TableCell>
              {numberToCurrencyString(row.receivedConsideration, {
                currency,
              })}
            </TableCell>
            <TableCell>
              {numberToCurrencyString(row.escrowConsideration, {
                currency,
              })}
            </TableCell>
            <TableCell>
              {numberToCurrencyString(row.earnoutConsideration, {
                currency,
              })}
            </TableCell>
            <TableCell>
              {numberToCurrencyString(row.withholdingTaxPayable, {
                currency,
              })}
            </TableCell>
          </StyledRow>
        ))}
      </TableBody>
      <FooterContainer>
        <TableRow>
          {isFirstTable && (
            <FooterCell colSpan={1} hasBorderRight align="left">
              {`Total ${dateToLabel(investmentAllocation[0].investmentDate)}`}
            </FooterCell>
          )}
          <FooterCell colSpan={1}></FooterCell>
          <FooterCell colSpan={1} align="left">
            {numberToCurrencyString(
              round(sumBy(investmentAllocation, 'receivedConsideration'), 2),
              { currency },
            )}
          </FooterCell>
          <FooterCell colSpan={1} align="left">
            {numberToCurrencyString(
              round(sumBy(investmentAllocation, 'escrowConsideration'), 2),
              { currency },
            )}
          </FooterCell>
          <FooterCell colSpan={1} align="left">
            {numberToCurrencyString(
              round(sumBy(investmentAllocation, 'earnoutConsideration'), 2),
              { currency },
            )}
          </FooterCell>
          <FooterCell colSpan={1} align="left">
            {numberToCurrencyString(
              round(sumBy(investmentAllocation, 'withholdingTaxPayable'), 2),
              { currency },
            )}
          </FooterCell>
        </TableRow>
      </FooterContainer>
    </StyledTable>
  );
};

export default InvestorAllocationsTable;
